/**
 * A contagem regressiva de um compromisso: o quanto falta, escrito de todas as
 * formas que o cartão de "Vem aí" precisa.
 *
 * É a aritmética espalhada por `lib/features/dashboard/presentation/widgets/
 * upcoming_countdowns.dart` — `_isImminent`, o rótulo do `_UrgencyPill`, as
 * quatro cápsulas do `_CountdownUnits` e a fração e o rótulo do
 * `_CountdownFooter` — reunida num lugar só. Lá cada peça refaz a subtração
 * com o seu próprio `DateTime.now()`, e em volta de uma virada de segundo elas
 * chegam a discordar entre si; aqui a hora entra uma vez e todas as respostas
 * saem dela.
 *
 * As palavras ficam como estão lá: `'AGORA'`, `'HOJE'` e `'TOMORROW'` na
 * pastilha (duas em português e uma em inglês, como no outro app), `'days'`,
 * `'hrs'`, `'min'` e `'sec'` nas cápsulas, e o rodapé em português.
 */

import { useEffect, useState } from 'react';

const SEGUNDO = 1000;
const MINUTO = 60 * SEGUNDO;
const HORA = 60 * MINUTO;
const DIA = 24 * HORA;

/** A janela do `getUpcomingEvents(days: 60)`, que é também a régua da barra. */
export const JANELA_DE_VEM_AI = 60;

/** O `_isImminent`: de agora até 48 horas à frente. */
const IMINENTE = 48 * HORA;

export interface Contagem {
  /** Quanto falta em milissegundos, nunca negativo — o `_remainingFor`. */
  restante: number;
  dias: number;
  /** As horas que sobram depois dos dias, o `inHours % 24`. */
  horas: number;
  minutos: number;
  segundos: number;
  /** O `showSeconds`: a cápsula dos segundos só entra na última volta. */
  mostraSegundos: boolean;
  /** O `_isImminent`, que acende o halo do cartão e o pontinho do selo. */
  iminente: boolean;
  /** O rótulo do `_UrgencyPill`, ou vazio quando não há pastilha. */
  urgencia: string;
  /** O `fraction` da barra do rodapé, de 0 a 1. */
  fracao: number;
  /** O `label` do rodapé: "faltam 3 dias", "falta 1 dia", "faltam 40min". */
  falta: string;
  /** O `isSoon`: um dia ou menos, que troca a cor da barra e o desenho. */
  perto: boolean;
}

/**
 * O `inDays`, `inHours` e companhia do Dart: divisão inteira em direção ao
 * zero. `Math.trunc` e não `Math.floor` porque o `_isImminent` pergunta pelo
 * passado, onde a diferença é negativa e as duas discordam.
 */
const emUnidades = (ms: number, unidade: number) => Math.trunc(ms / unidade);

export function contagemPara(quando: Date, agora = new Date()): Contagem {
  const bruto = quando.getTime() - agora.getTime();
  const restante = Math.max(0, bruto);

  const dias = emUnidades(restante, DIA);
  const horasCheias = emUnidades(restante, HORA);
  const minutosCheios = emUnidades(restante, MINUTO);

  // A pastilha lê a diferença crua, e não a aparada: é o que separa "AGORA"
  // de um compromisso que já passou e não tem pastilha nenhuma.
  const horasCruas = emUnidades(bruto, HORA);
  const iminente = bruto >= 0 && bruto < IMINENTE;

  const urgencia = !iminente
    ? ''
    : horasCruas < 24
      ? horasCruas < 1
        ? 'AGORA'
        : 'HOJE'
      : 'TOMORROW';

  const falta =
    dias === 0
      ? horasCheias === 0
        ? `faltam ${minutosCheios}min`
        : `faltam ${horasCheias}h`
      : dias === 1
        ? 'falta 1 dia'
        : `faltam ${dias} dias`;

  return {
    restante,
    dias,
    horas: horasCheias % 24,
    minutos: minutosCheios % 60,
    segundos: emUnidades(restante, SEGUNDO) % 60,
    mostraSegundos: horasCheias < 24 && restante > 0,
    iminente,
    urgencia,
    fracao: Math.min(1, Math.max(0, 1 - restante / (JANELA_DE_VEM_AI * DIA))),
    falta,
    perto: dias <= 1,
  };
}

/**
 * A contagem que anda sozinha: o `_schedule` de lá, que bate de segundo em
 * segundo na última volta e de minuto em minuto no resto do tempo.
 *
 * Fica por cartão, e não por seção, porque é assim que o Flutter a monta: um
 * relógio no alto redesenharia as três contagens e a lista de compromissos
 * inteira a cada batida, para mudar dois dígitos de um cartão só.
 */
export function usarContagem(quando: Date): Contagem {
  const [agora, setAgora] = useState(() => new Date());
  const contagem = contagemPara(quando, agora);
  const passo = contagem.restante < DIA ? SEGUNDO : MINUTO;

  useEffect(() => {
    const relogio = setInterval(() => setAgora(new Date()), passo);
    return () => clearInterval(relogio);
  }, [passo]);

  return contagem;
}
